import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  VStack,
  HStack,
  IconButton,
  useToast,
  Text,
  Heading,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Textarea,
  Switch,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Flex,
  Spacer,
  InputGroup,
  InputLeftElement,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Link,
  Tooltip,
} from '@chakra-ui/react';
import { 
  EditIcon, 
  DeleteIcon, 
  AddIcon, 
  SearchIcon, 
  ExternalLinkIcon,
  InfoIcon,
  WarningIcon,
  CheckIcon,
  CloseIcon
} from '@chakra-ui/icons';
import { 
  FaShieldAlt, 
  FaPhone, 
  FaGlobe, 
  FaBuilding,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaFilter,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { useOutletContext } from 'react-router-dom';

const InsuranceManagement = () => {
  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deleteInsurance, setDeleteInsurance] = useState(null);
  
  const toast = useToast();
  const { adminUser } = useOutletContext();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'Commercial',
    description: '',
    website_url: '',
    phone_number: '',
    fax_number: '',
    email: '',
    is_active: true,
    notes: ''
  });

  const [formErrors, setFormErrors] = useState({});

  const fetchInsurances = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('insurances')
        .select('*')
        .order('name');

      if (error) throw error;
      setInsurances(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching insurances',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchInsurances();
  }, [fetchInsurances]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Insurance name is required';
    }
    
    if (!formData.code.trim()) {
      errors.code = 'Insurance code is required';
    }
    
    if (!formData.type) {
      errors.type = 'Insurance type is required';
    }

    // Check for duplicate name
    const existingInsurance = insurances.find(insurance => 
      insurance.name.toLowerCase() === formData.name.toLowerCase() && 
      insurance.id !== selectedInsurance?.id
    );
    
    if (existingInsurance) {
      errors.name = 'An insurance with this name already exists';
    }

    // Check for duplicate code
    const existingCode = insurances.find(insurance => 
      insurance.code.toLowerCase() === formData.code.toLowerCase() && 
      insurance.id !== selectedInsurance?.id
    );
    
    if (existingCode) {
      errors.code = 'An insurance with this code already exists';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (selectedInsurance) {
        // Update existing insurance
        const { error } = await supabase
          .from('insurances')
          .update(formData)
          .eq('id', selectedInsurance.id);

        if (error) throw error;

        toast({
          title: 'Insurance updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new insurance
        const { error } = await supabase
          .from('insurances')
          .insert([formData]);

        if (error) throw error;

        toast({
          title: 'Insurance created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      onClose();
      resetForm();
      fetchInsurances();
    } catch (error) {
      toast({
        title: 'Error saving insurance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEdit = (insurance) => {
    setSelectedInsurance(insurance);
    setFormData({
      name: insurance.name || '',
      code: insurance.code || '',
      type: insurance.type || 'Commercial',
      description: insurance.description || '',
      website_url: insurance.website_url || '',
      phone_number: insurance.phone_number || '',
      fax_number: insurance.fax_number || '',
      email: insurance.email || '',
      is_active: insurance.is_active !== false,
      notes: insurance.notes || ''
    });
    onOpen();
  };

  const handleDelete = async () => {
    if (!deleteInsurance) return;

    try {
      // Check if insurance is being used in submissions
      const { data: submissions, error: submissionsError } = await supabase
        .from('submissions')
        .select('id')
        .eq('insurance_id', deleteInsurance.id)
        .limit(1);

      if (submissionsError) throw submissionsError;

      if (submissions && submissions.length > 0) {
        toast({
          title: 'Cannot delete insurance',
          description: 'This insurance is being used in existing submissions. Deactivate it instead.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const { error } = await supabase
        .from('insurances')
        .delete()
        .eq('id', deleteInsurance.id);

      if (error) throw error;

      toast({
        title: 'Insurance deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchInsurances();
    } catch (error) {
      toast({
        title: 'Error deleting insurance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDeleteClose();
      setDeleteInsurance(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      type: 'Commercial',
      description: '',
      website_url: '',
      phone_number: '',
      fax_number: '',
      email: '',
      is_active: true,
      notes: ''
    });
    setSelectedInsurance(null);
    setFormErrors({});
  };

  const handleAddNew = () => {
    resetForm();
    onOpen();
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  // Filter insurances based on search and filters
  const filteredInsurances = insurances.filter(insurance => {
    const matchesSearch = insurance.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         insurance.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (insurance.description && insurance.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = typeFilter === 'all' || insurance.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && insurance.is_active) ||
                         (statusFilter === 'inactive' && !insurance.is_active);
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: insurances.length,
    active: insurances.filter(i => i.is_active).length,
    inactive: insurances.filter(i => !i.is_active).length,
    commercial: insurances.filter(i => i.type === 'Commercial').length,
    medicare: insurances.filter(i => i.type === 'Medicare').length,
    medicaid: insurances.filter(i => i.type === 'Medicaid').length,
    selfPay: insurances.filter(i => i.type === 'Self-Pay').length,
  };

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('md', 'dark-lg');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box px={{ base: 2, md: 8 }} py={8} bg={bgColor} minH="100vh">
      <Flex justify="space-between" align="center" mb={8}>
        <Heading as="h1" size="2xl" fontWeight="bold">
          Insurance Management
        </Heading>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={handleAddNew}
          size="lg"
        >
          Add Insurance
        </Button>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mb={8}>
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>Total Insurances</StatLabel>
              <StatNumber>{stats.total}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>Active</StatLabel>
              <StatNumber color="green.500">{stats.active}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>Commercial</StatLabel>
              <StatNumber color="blue.500">{stats.commercial}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={cardBg} boxShadow={cardShadow}>
          <CardBody>
            <Stat>
              <StatLabel>Medicare</StatLabel>
              <StatNumber color="purple.500">{stats.medicare}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Card bg={cardBg} boxShadow={cardShadow} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <FormControl>
              <FormLabel>Search</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, code, or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Type</FormLabel>
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">All Types</option>
                <option value="Commercial">Commercial</option>
                <option value="Medicare">Medicare</option>
                <option value="Medicaid">Medicaid</option>
                <option value="Self-Pay">Self-Pay</option>
                <option value="Government">Government</option>
                <option value="Other">Other</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Insurance Table */}
      <Card bg={cardBg} boxShadow={cardShadow}>
        <CardHeader>
          <Heading size="md">Insurance Providers ({filteredInsurances.length})</Heading>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Contact</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredInsurances.map((insurance) => (
                  <Tr key={insurance.id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold">{insurance.name}</Text>
                        {insurance.description && (
                          <Text fontSize="sm" color="gray.500">
                            {insurance.description}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue" variant="outline">
                        {insurance.code}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={
                          insurance.type === 'Commercial' ? 'blue' :
                          insurance.type === 'Medicare' ? 'purple' :
                          insurance.type === 'Medicaid' ? 'green' :
                          insurance.type === 'Self-Pay' ? 'orange' :
                          'gray'
                        }
                      >
                        {insurance.type}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={insurance.is_active ? 'green' : 'red'}>
                        {insurance.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        {insurance.phone_number && (
                          <HStack spacing={2}>
                            <FaPhone size={12} />
                            <Text fontSize="sm">{insurance.phone_number}</Text>
                          </HStack>
                        )}
                        {insurance.website_url && (
                          <HStack spacing={2}>
                            <FaGlobe size={12} />
                            <Link href={insurance.website_url} isExternal fontSize="sm">
                              Website
                            </Link>
                          </HStack>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="Edit Insurance">
                          <IconButton
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleEdit(insurance)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete Insurance">
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => {
                              setDeleteInsurance(insurance);
                              onDeleteOpen();
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={handleClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedInsurance ? 'Edit Insurance' : 'Add New Insurance'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl isInvalid={!!formErrors.name}>
                    <FormLabel>Insurance Name *</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Blue Cross Blue Shield"
                    />
                    <FormErrorMessage>{formErrors.name}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!formErrors.code}>
                    <FormLabel>Insurance Code *</FormLabel>
                    <Input
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder="e.g., BCBS"
                    />
                    <FormErrorMessage>{formErrors.code}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl isInvalid={!!formErrors.type}>
                    <FormLabel>Type *</FormLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                    >
                      <option value="Commercial">Commercial</option>
                      <option value="Medicare">Medicare</option>
                      <option value="Medicaid">Medicaid</option>
                      <option value="Self-Pay">Self-Pay</option>
                      <option value="Government">Government</option>
                      <option value="Other">Other</option>
                    </Select>
                    <FormErrorMessage>{formErrors.type}</FormErrorMessage>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <HStack>
                      <Switch
                        name="is_active"
                        isChecked={formData.is_active}
                        onChange={handleInputChange}
                        colorScheme="green"
                      />
                      <Text>{formData.is_active ? 'Active' : 'Inactive'}</Text>
                    </HStack>
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the insurance provider..."
                    rows={3}
                  />
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <Input
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      placeholder="e.g., 1-800-123-4567"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Fax Number</FormLabel>
                    <Input
                      name="fax_number"
                      value={formData.fax_number}
                      onChange={handleInputChange}
                      placeholder="e.g., 1-800-123-4568"
                    />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="e.g., support@insurance.com"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Website URL</FormLabel>
                    <Input
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      placeholder="e.g., https://www.insurance.com"
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Internal notes about this insurance provider..."
                    rows={3}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="blue">
                {selectedInsurance ? 'Update' : 'Create'} Insurance
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Insurance</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              <AlertTitle>Are you sure?</AlertTitle>
              <AlertDescription>
                This will permanently delete "{deleteInsurance?.name}". This action cannot be undone.
              </AlertDescription>
            </Alert>
            <Text>
              If this insurance is being used in existing submissions, it cannot be deleted. 
              Consider deactivating it instead.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDelete}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InsuranceManagement;
